/**
 * District accounting codes.
 *
 * Nine hyphen-delimited segments. Two patterns in use (REQUIREMENTS.md §5.2):
 *
 *   Main fund   10-2818-580-000-00-000-000-329-0000
 *   Grant fund  10-0499-000-150-00-000-000-150-0000
 *
 * The full form is what the business office requires on every submitted
 * document and must never be abbreviated (§8.1). The space-delimited form used
 * by the legacy Sheets system is deprecated but still accepted on input, since
 * it may be pasted in from old records.
 */

/** Number of segments in a well-formed account code. */
export const SEGMENT_COUNT = 9

/** Expected digit width of each segment, by position. */
const SEGMENT_WIDTHS = [2, 4, 3, 3, 2, 3, 3, 3, 4] as const

const CANONICAL = /^\d{2}-\d{4}-\d{3}-\d{3}-\d{2}-\d{3}-\d{3}-\d{3}-\d{4}$/

/**
 * Normalize to canonical hyphen-delimited form.
 *
 * Accepts hyphens, spaces, or a mix, and tolerates surrounding whitespace.
 * Returns null when the input isn't a plausible account code — callers decide
 * whether that's a validation error or a no-match.
 */
export function normalizeAccountCode(input: string): string | null {
  const segments = input.trim().split(/[\s-]+/).filter(Boolean)
  if (segments.length !== SEGMENT_COUNT) return null
  if (!segments.every((s) => /^\d+$/.test(s))) return null

  const padded = segments.map((s, i) => s.padStart(SEGMENT_WIDTHS[i] ?? s.length, '0'))
  if (padded.some((s, i) => s.length !== SEGMENT_WIDTHS[i])) return null

  return padded.join('-')
}

/** True when `input` is a well-formed account code in any accepted form. */
export function isValidAccountCode(input: string): boolean {
  return normalizeAccountCode(input) !== null
}

/** True only for the canonical stored form — no spaces, no short segments. */
export function isCanonicalAccountCode(input: string): boolean {
  return CANONICAL.test(input)
}

/** Split a code into its nine segments. Returns [] if the code is malformed. */
export function accountCodeSegments(input: string): string[] {
  const normalized = normalizeAccountCode(input)
  return normalized === null ? [] : normalized.split('-')
}

/**
 * Human-readable explanation of why a code was rejected, for form validation.
 * Returns null when the code is valid.
 */
export function accountCodeError(input: string): string | null {
  const trimmed = input.trim()
  if (trimmed === '') return 'Account code is required.'

  const segments = trimmed.split(/[\s-]+/).filter(Boolean)
  if (segments.some((s) => !/^\d+$/.test(s))) {
    return 'Account codes contain only digits and hyphens.'
  }
  if (segments.length !== SEGMENT_COUNT) {
    return `Expected ${SEGMENT_COUNT} segments, found ${segments.length}. Example: 10-2818-580-000-00-000-000-329-0000`
  }
  if (normalizeAccountCode(trimmed) === null) {
    const bad = segments
      .map((s, i) => ({ s, i, want: SEGMENT_WIDTHS[i] ?? 0 }))
      .filter(({ s, want }) => s.length > want)
      .map(({ i, s, want }) => `segment ${i + 1} ("${s}") is longer than ${want} digits`)
    return `Segment lengths don't match the expected pattern: ${bad.join(', ')}.`
  }
  return null
}
